'use client';
import React, { useEffect, useMemo, useState } from 'react';
import { StyledIndustryContainer } from '@/components/common/ygen/sections/industry/industry-container.style';
import { StyledIndustry } from '@/components/common/ygen/sections/industry/industry.style';
import { Col, Container, Row } from 'react-bootstrap';
import Title from '@/components/common/ui/text/Title';
import Button from '@/components/common/ui/buttons/Button';
import LeftSVGScroll from '@/components/common/ui/buttons/LeftSVGScroll';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Mousewheel, Pagination } from 'swiper/modules';
import useContainerOffset from '@/components/hooks/useContainerOffset';
import useDeviceTypeMobile from '@/components/hooks/useDeviceTypeMobile';
import Prev from '@/components/common/ui/buttons/Prev';
import Next from '@/components/common/ui/buttons/Next';
import CaseStudySingle from '@/components/common/ui/single-item/CaseStudySingle';
import reactHtmlParser from 'react-html-parser';
import { usePathname } from 'next/navigation';


const IndustryV2 = ({ data, version, color, mainIndex, getIndustryDataALL, getImpactStoriesDataALL, getSolutionDataALL  }) => {
	const isMobile = useDeviceTypeMobile();
	const offset = useContainerOffset('.container');
	const pathName = usePathname();

	// Memoize the data to prevent unnecessary re-renders
	const [getIndustryData, setIndustryData] = useState(() => getIndustryDataALL);
	const [getImpactData, setImpactData] = useState(() => getImpactStoriesDataALL);
	const [getSolutionData, setSolutionData] = useState(() => getSolutionDataALL);

	// Memoize the data update effect
	useEffect(() => {
		let mounted = true;

		const updateData = () => {
			if (!mounted) return;

			if (getIndustryDataALL) {
				setIndustryData(getIndustryDataALL);
			}
			if (getImpactStoriesDataALL) {
				setImpactData(getImpactStoriesDataALL);
			}
			if (getSolutionDataALL) {
				setSolutionData(getSolutionDataALL);
			}
		};

		updateData();

		return () => {
			mounted = false;
		};
	}, []);

	// Memoize the subtitle and description to prevent unnecessary re-renders
	const subtitle = useMemo(() => (
		data?.section_data?.subtitle || 'Real Results, Proven Success'
	), [data?.section_data?.subtitle]);

	const description = useMemo(() => (
		data?.section_data?.description || 'See real-world examples of how our <i>solutions</i> transform businesses and <i>drive growth</i>.'
	), [data?.section_data?.description]);

	// Memoize the button props to prevent unnecessary re-renders
	const buttonProps = useMemo(() => ({
		color: '#ffffff' ,
		hoverBackground: undefined,
		hoverColor: '#152637' ,
		background:  '#FFFFFF' ,
		pathColor:  '#DD223F' ,
		text:  'View All Case Studies' ,
		src: data?.section_data?.short_desc || '/',
	}), [version, data?.section_data?.short_desc]);

	// Memoize the Swiper breakpoints to prevent unnecessary re-renders
	const swiperBreakpoints = useMemo(() => ({
		320: {
			slidesPerView: 1.2,
			spaceBetween: 30,
			mousewheel: false,
			navigation: {
				prevEl: version === 2 || version === 3 ? '#case-study-prev' : '#industry-prev',
				nextEl: version === 2 || version === 3 ? '#case-study-next' : '#industry-next',
			},
		},
		768: {
			slidesPerView: 3.5,
			spaceBetween: 30,
		},
		1024: {
			slidesPerView: 4.2,
			spaceBetween: 30,
		},
	}), [version]);

	// Memoize the mousewheel config to prevent unnecessary re-renders
	const mousewheelConfig = useMemo(() => ({
		releaseOnEdges: true,
		sensitivity: 1.5,
		thresholdDelta: 1.5,
		thresholdTime: 5,
		eventsTarget: `.industry-scroll-wrapper-main-${mainIndex}`,
	}), [mainIndex]);
	// Memoize the container style to prevent unnecessary re-renders

	return (
		<StyledIndustryContainer data-scroll-section offset={offset} className={`main-industry industry-scroll-wrapper-main-${mainIndex} version_two`}>
			<StyledIndustry id="okay-hero" offset={offset} className={`version_two industry_footprintss`}>
				<div className={`industry-container industry-container-scroll`}>
					<LeftSVGScroll />
					<Container>
						<Row className={'mb80'}>
							<Col md={3}>
								<p data-scroll data-scroll-repeat className={'subtitle-ygen fade-up'}>
									{subtitle}
								</p>
							</Col>
							<Col md={6}>
								<Title
									tag={'h2'}
									animClass={'fade-up'}
									text={description}
									color={'#fff'}
								/>
							</Col>
							<Col md={3} className={'d-flex justify-content-end mobile-justify'}>
								{!isMobile && <Button {...buttonProps} />}
								<div className="d-flex navigation-item">
									<Prev id={'case-study-prev'} />
									<Next id={'case-study-next'} />
								</div>
							</Col>
						</Row>
					</Container>

					<Container >
						<Row>
							{
								getImpactData?.child_pages?.list?.length > 0 && (
									<Col md={12}>
										<Swiper
											slidesPerView={ 4}
											spaceBetween={30}
											slidesPerGroup={4}
											loop={false}
											autoplay={false}
											speed={1500}
											pagination={false}
											mousewheel={mousewheelConfig}
											navigation={{
												prevEl: version === 2 ? '#case-study-prev' : '#industry-prev',
												nextEl: version === 2 ? '#case-study-next' : '#industry-next',
											}}
											breakpoints={swiperBreakpoints}
											modules={[Mousewheel, Pagination]}
											className="about-slider_mask w-slider-mask"
										>
											{getImpactData?.child_pages?.list?.map((e, index) => {
												const thumb = e?.images?.list?.find((f) => f?.thumb === 'on');
												return (
													<SwiperSlide key={index}>
														<CaseStudySingle
															hover={'#152637'}
															noPara
															link={`/impact-stories/${e?.page_data?.slug}`}
															subColor={'rgba(255,255,255,0.5)'}
															color={pathName === '/' ? 'white' : data?.section_data?.short_desc || color || 'white'}
															img={thumb?.full_path}
															title={e?.page_data?.title}
															subject={reactHtmlParser(e?.types?.title)}
														/>
													</SwiperSlide>
												);
											})}
										</Swiper>
									</Col>
								)

							}
						</Row>
					</Container>

					{isMobile && (
						<Container>
							<Row>
								<Col md={12}>
									<Button src={data?.section_data?.short_desc} margin={'30px 0 0'}  {...buttonProps} />
								</Col>
							</Row>
						</Container>
					)}
				</div>
			</StyledIndustry>
		</StyledIndustryContainer>
	);
};


export default React.memo(IndustryV2);
